import { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { CreditCard, DollarSign, Cpu, ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { fetchCostsBySessionModel, fetchWallet, fetchCreditTransactions, fetchCreditPackages, createCheckout } from '../api/client'
import type { CostBySessionModelRow, CreditWallet, CreditTransaction, CreditPackage } from '../api/types'
import { useOrgContext } from '../state/OrgContext'
import { formatDisplayedUsd } from '../costDisplay'
import { formatDateReadable } from '../dateDisplay'

const formatNumber = (n: number) => n.toLocaleString()

export default function CostBreakdownPage() {
  const { t, i18n } = useTranslation('pages')

  const [adminState, setAdminState] = useState<{
    status: 'loading' | 'ready' | 'error'
    rows: CostBySessionModelRow[]
    totalCost: number
    message?: string
  }>({ status: 'loading', rows: [], totalCost: 0 })

  const [wallet, setWallet] = useState<CreditWallet | null>(null)
  const [txState, setTxState] = useState<{
    status: 'loading' | 'ready' | 'error'
    rows: CreditTransaction[]
    message?: string
  }>({ status: 'loading', rows: [] })
  const [packages, setPackages] = useState<CreditPackage[]>([])
  const [checkoutBusy, setCheckoutBusy] = useState<number | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const { activeRole } = useOrgContext()

  const USAGE_TYPE_LABELS: Record<string, string> = useMemo(() => ({
    PAPER_GRADING: t('costBreakdown.usageBadge'),
    RUBRIC_PROCESSING: t('costBreakdown.colType'),
    QUESTION_EXTRACTION: t('costBreakdown.colExam'),
    CLASS_ANALYSIS: t('costBreakdown.colExam2'),
  }), [t])

  const canBuy = activeRole === 'ORG_MANAGER' || activeRole === 'ADMIN'

  // Result of returning from a hosted checkout (?payment=success|cancel).
  const paymentResult = useMemo(() => {
    if (typeof window === 'undefined') return null
    const p = new URLSearchParams(window.location.search).get('payment')
    return p === 'success' || p === 'cancel' ? p : null
  }, [])

  if (activeRole === 'TEACHER') return <Navigate to="/exams" replace />

  async function handleBuy(pkg: CreditPackage) {
    setCheckoutError(null)
    setCheckoutBusy(pkg.id)
    try {
      const { checkout_url } = await createCheckout(pkg.id)
      // Redirect to the provider's hosted checkout page.
      window.location.assign(checkout_url)
    } catch (err) {
      setCheckoutError((err as Error).message || t('costBreakdown.paymentError'))
      setCheckoutBusy(null)
    }
  }

  useEffect(() => {
    if (activeRole === 'ADMIN') {
      fetchCostsBySessionModel()
        .then((data) => setAdminState({ status: 'ready', rows: data.rows || [], totalCost: Number(data.total_cost || 0) }))
        .catch((err) => setAdminState({ status: 'error', rows: [], totalCost: 0, message: err.message || t('costBreakdown.loadError') }))
    }
  }, [activeRole])

  useEffect(() => {
    fetchWallet()
      .then((data) => setWallet(data))
      .catch(() => setWallet(null))

    fetchCreditTransactions()
      .then((data) => setTxState({ status: 'ready', rows: data.transactions || [] }))
      .catch((err) => setTxState({ status: 'error', rows: [], message: err.message || t('costBreakdown.transactionsLoadError') }))
  }, [])

  useEffect(() => {
    if (!canBuy) return
    fetchCreditPackages()
      .then((data) => setPackages(data.packages || []))
      .catch(() => setPackages([]))
  }, [canBuy])

  /** Per-exam grouping from transactions — used by both roles */
  const perExam = useMemo(() => {
    const map = new Map<string, { credits: number; computeCost: number }>()
    for (const tx of txState.rows) {
      if (tx.type !== 'AI_SPEND') continue
      const key = tx.exam_name ?? '—'
      const cur = map.get(key) ?? { credits: 0, computeCost: 0 }
      cur.credits += Math.abs(tx.credit_amount ?? 0)
      cur.computeCost += tx.compute_cost ?? 0
      map.set(key, cur)
    }
    return Array.from(map.entries())
      .map(([exam_name, v]) => ({ exam_name, ...v }))
      .sort((a, b) => a.exam_name.localeCompare(b.exam_name))
  }, [txState.rows])

  const totalCredits = perExam.reduce((s, x) => s + x.credits, 0)
  const totalComputeCost = perExam.reduce((s, x) => s + x.computeCost, 0)

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">{t('costBreakdown.title')}</h2>
        <p className="page-header-sub">
          {activeRole === 'ADMIN'
            ? t('costBreakdown.subtitleAdmin')
            : t('costBreakdown.subtitleManager')}
        </p>
      </div>

      {/* Credits balance */}
      <div className="card card-padding mb-24">
        <div className="flex items-center gap-8 mb-20">
          <CreditCard size={18} style={{ color: 'var(--accent-copper)' }} />
          <h3 className="card-title">{t('costBreakdown.creditsCard')}</h3>
        </div>

        <div className="stat-card mb-20">
          <div className="stat-card-value">{wallet ? wallet.read_balance.toFixed(1) : '-'}</div>
          <div className="stat-card-label">{t('costBreakdown.remainingLabel')}</div>
        </div>

        <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
          {t('costBreakdown.creditsDescription')}
        </p>

        {paymentResult === 'success' && (
          <p className="text-sm" style={{ color: 'var(--color-success)', marginBottom: 12 }}>
            {t('costBreakdown.paymentSuccess')}
          </p>
        )}
        {paymentResult === 'cancel' && (
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            {t('costBreakdown.paymentCancelled')}
          </p>
        )}

        {canBuy && (
          <div style={{ marginBottom: 8 }}>
            <h4 className="text-sm font-semibold mb-12">{t('costBreakdown.buySection')}</h4>
            {checkoutError && (
              <p className="text-sm" style={{ color: 'var(--color-danger)', marginBottom: 8 }}>{checkoutError}</p>
            )}
            {packages.length === 0 ? (
              <p className="text-sm text-muted">{t('costBreakdown.noPackages')}</p>
            ) : (
              <div className="flex gap-12" style={{ flexWrap: 'wrap' }}>
                {packages.map((pkg) => (
                  <div key={pkg.id} className="card card-padding" style={{ minWidth: 180, flex: '1 1 180px' }}>
                    <div className="font-heading font-semibold" style={{ fontSize: 16 }}>{pkg.name}</div>
                    <div className="stat-card-value" style={{ margin: '8px 0' }}>{pkg.reads.toLocaleString()}</div>
                    <div className="stat-card-label" style={{ marginBottom: 12 }}>{t('costBreakdown.creditsUnit')}</div>
                    <div className="text-sm font-semibold" style={{ marginBottom: 12 }}>
                      {pkg.price.toFixed(2)} {pkg.currency}
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      disabled={checkoutBusy !== null}
                      onClick={() => handleBuy(pkg)}
                    >
                      {checkoutBusy === pkg.id ? t('costBreakdown.buyRedirecting') : t('costBreakdown.buy')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <h4 className="text-sm font-semibold mb-12" style={{ marginTop: 20 }}>{t('costBreakdown.transactionsSection')}</h4>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>{t('costBreakdown.colDate')}</th>
                <th>{t('costBreakdown.colType')}</th>
                <th>{t('costBreakdown.colTransaction')}</th>
                <th>{t('costBreakdown.colExam')}</th>
                <th style={{ textAlign: 'right' }}>{t('costBreakdown.colCredit')}</th>
                <th style={{ textAlign: 'right' }}>{t('costBreakdown.colUsd')}</th>
              </tr>
            </thead>
            <tbody>
              {txState.status === 'loading' && (
                <tr><td colSpan={6} className="text-muted text-sm">{t('costBreakdown.rowLoading')}</td></tr>
              )}
              {txState.status === 'error' && (
                <tr><td colSpan={6} className="text-sm" style={{ color: 'var(--color-danger)' }}>{txState.message}</td></tr>
              )}
              {txState.status === 'ready' && txState.rows.length === 0 && (
                <tr><td colSpan={6} className="text-muted text-sm">{t('costBreakdown.rowEmpty')}</td></tr>
              )}
              {txState.status === 'ready' &&
                txState.rows.map((tx) => {
                  const creditAmt = tx.credit_amount ?? 0
                  const isPositive = creditAmt > 0
                  const isTopup = tx.type === 'TOPUP' || tx.type === 'PURCHASE' || tx.type === 'ADJUST'
                  return (
                    <tr key={tx.id}>
                      <td className="text-muted text-sm">{formatDateReadable(tx.created_at, i18n.language)}</td>
                      <td>
                        {isTopup ? (
                          <span className="badge badge-success"><ArrowDownRight size={12} />{t('costBreakdown.uploadBadge')}</span>
                        ) : (
                          <span className="badge badge-warning"><ArrowUpRight size={12} />{t('costBreakdown.usageBadge')}</span>
                        )}
                      </td>
                      <td className="text-sm text-muted">
                        {tx.usage_type ? (USAGE_TYPE_LABELS[tx.usage_type] ?? tx.usage_type) : '—'}
                      </td>
                      <td className="text-sm">{tx.exam_name ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="font-heading font-semibold" style={{ color: isPositive ? 'var(--color-success)' : 'var(--text-primary)' }}>
                          {isPositive ? '+' : ''}{Math.abs(creditAmt).toFixed(2)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }} className="text-sm text-muted">
                        {tx.type === 'AI_SPEND' && tx.compute_cost != null
                          ? formatDisplayedUsd(tx.compute_cost)
                          : tx.monetary_amount != null
                            ? `$${tx.monetary_amount.toFixed(2)}`
                            : '—'}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cost by Exam */}
      <div className="card card-padding mb-24">
        <div className="flex items-center gap-8 mb-16">
          <DollarSign size={18} style={{ color: 'var(--accent-copper)' }} />
          <h3 className="card-title">{t('costBreakdown.usageByExam')}</h3>
          <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
            {t('costBreakdown.totalLabel')}{' '}
            <span className="font-heading font-semibold" style={{ color: 'var(--text-primary)' }}>
              {totalCredits.toFixed(2)} {t('costBreakdown.creditsUnit2')}
            </span>
            {' · '}
            <span className="text-muted">{formatDisplayedUsd(totalComputeCost)}</span>
          </span>
        </div>
        {txState.status === 'loading' && <p className="text-sm text-muted">{t('costBreakdown.usageLoading')}</p>}
        {txState.status === 'ready' && perExam.length === 0 && (
          <p className="text-sm text-muted">{t('costBreakdown.usageEmpty')}</p>
        )}
        {txState.status === 'ready' && perExam.length > 0 && (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('costBreakdown.colExam2')}</th>
                  <th style={{ textAlign: 'right' }}>{t('costBreakdown.colReadingCredit')}</th>
                  <th style={{ textAlign: 'right' }}>{t('costBreakdown.colUsd2')}</th>
                </tr>
              </thead>
              <tbody>
                {perExam.map((item) => (
                  <tr key={item.exam_name}>
                    <td className="font-semibold">{item.exam_name}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="font-heading font-semibold">{item.credits.toFixed(2)} {t('costBreakdown.creditsUnit2')}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="text-muted text-sm">{formatDisplayedUsd(item.computeCost)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Exam + Model Detail — only for ADMIN */}
      {activeRole === 'ADMIN' && (
        <div className="card card-padding">
          <div className="flex items-center gap-8 mb-16">
            <Cpu size={18} style={{ color: 'var(--accent-copper)' }} />
            <h3 className="card-title">{t('costBreakdown.detailTitle')}</h3>
          </div>
          {adminState.status === 'ready' && adminState.rows.length > 0 ? (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('costBreakdown.colExam2')}</th>
                    <th>{t('costBreakdown.colProvider')}</th>
                    <th>{t('costBreakdown.colModel')}</th>
                    <th style={{ textAlign: 'right' }}>{t('costBreakdown.colCalls')}</th>
                    <th style={{ textAlign: 'right' }}>{t('costBreakdown.colInputTokens')}</th>
                    <th style={{ textAlign: 'right' }}>{t('costBreakdown.colOutputTokens')}</th>
                    <th style={{ textAlign: 'right' }}>{t('costBreakdown.colCost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {adminState.rows.map((r) => (
                    <tr key={`${r.session_id}-${r.provider}-${r.model}`}>
                      <td className="font-semibold">{r.session_name}</td>
                      <td><span className="badge badge-neutral">{r.provider || '-'}</span></td>
                      <td className="text-sm text-muted">{r.model || '-'}</td>
                      <td style={{ textAlign: 'right' }} className="text-sm">{r.calls}</td>
                      <td style={{ textAlign: 'right' }} className="text-sm">{formatNumber(r.input_tokens)}</td>
                      <td style={{ textAlign: 'right' }} className="text-sm">{formatNumber(r.output_tokens)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="font-heading font-semibold">{formatDisplayedUsd(Number(r.cost_usd || 0))}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : adminState.status === 'ready' ? (
            <p className="text-sm text-muted">{t('costBreakdown.detailEmpty')}</p>
          ) : null}
        </div>
      )}
    </div>
  )
}
